// src/cockpit4/components/InvestmentFinancingSupport.tsx
// ============================================
// 投融资支持（财务中心 P2-功能10）- v2.0 详细改进计划
// 融资需求测算 / 投资回报分析(NPV/IRR) / 估值模型 / 融资材料 / 投资人报告
// 数据来源：localStorage（xeaos_investment_financing）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Banknote, TrendingUp, Scale, FileText, RefreshCw } from "lucide-react";

interface FinancingRound {
  id: string;
  stage: string;
  target: number;
  raised: number;
  valuation: number;
  dilution: number;
  runway: number;
  status: "进行中" | "已完成" | "规划中";
}

const INITIAL_ROUNDS: FinancingRound[] = [
  { id: "f1", stage: "天使轮", target: 100, raised: 100, valuation: 800, dilution: 12, runway: 18, status: "已完成" },
  { id: "f2", stage: "A 轮", target: 500, raised: 0, valuation: 4000, dilution: 11, runway: 24, status: "进行中" },
];

const NPV_DATA = [
  { year: 1, revenue: 180, cost: 120, fcf: 45 },
  { year: 2, revenue: 320, cost: 200, fcf: 90 },
  { year: 3, revenue: 520, cost: 300, fcf: 170 },
  { year: 4, revenue: 780, cost: 420, fcf: 270 },
  { year: 5, revenue: 1100, cost: 560, fcf: 400 },
];

const KEY = "xeaos_investment_financing";

function load(): FinancingRound[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as FinancingRound[];
  } catch { /* ignore */ }
  return INITIAL_ROUNDS;
}

export default function InvestmentFinancingSupport() {
  const [rounds, setRounds] = useState<FinancingRound[]>(load);
  const [showReport, setShowReport] = useState(false);

  const stats = useMemo(() => {
    const totalRaised = rounds.reduce((a, r) => a + r.raised, 0);
    const activeTarget = rounds.filter((r) => r.status === "进行中").reduce((a, r) => a + r.target, 0);
    const avgValuation = rounds.filter((r) => r.valuation > 0).reduce((a, r) => a + r.valuation, 0) / rounds.filter((r) => r.valuation > 0).length;
    return { totalRaised, activeTarget, avgValuation };
  }, [rounds]);

  // 简化 DCF 估值：按 15% 折现率
  const dcf = useMemo(() => {
    const rate = 0.15;
    let pv = 0;
    NPV_DATA.forEach((d) => { pv += d.fcf / Math.pow(1 + rate, d.year); });
    const terminal = (NPV_DATA[NPV_DATA.length - 1].fcf * 1.05) / (0.15 - 0.05) / Math.pow(1 + rate, 5);
    return Math.round(pv + terminal);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Banknote size={14} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">投融资支持</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · Investment</span>
        <button
          onClick={() => setShowReport((v) => !v)}
          className="flex items-center gap-1 ml-auto px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-300 hover:bg-emerald-500/20"
        >
          <FileText size={10} /> 生成投资人报告
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="累计融资" value={`¥${stats.totalRaised}万`} color="#34D399" />
        <MetricCard label="A轮目标" value={`¥${stats.activeTarget}万`} color="#38BDF8" />
        <MetricCard label="平均估值" value={`¥${stats.avgValuation.toFixed(0)}万`} color="#A78BFA" />
        <MetricCard label="DCF 估值" value={`¥${(dcf / 100).toFixed(0)}万`} color="#FBBF24" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Scale size={11} className="text-emerald-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">融资轮次</span>
        </div>
        <div className="space-y-1.5">
          {rounds.map((r) => (
            <div key={r.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{r.stage}</span>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                  r.status === "已完成" ? "bg-emerald-500/15 text-emerald-400" : r.status === "进行中" ? "bg-sky-500/15 text-sky-400" : "bg-gray-500/15 text-gray-500"
                }`}>{r.status}</span>
                <span className="ml-auto text-[10px] font-mono text-emerald-400">¥{r.raised}/{r.target}万</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${(r.raised / Math.max(1, r.target)) * 100}%`, background: r.status === "已完成" ? "#34D399" : "#38BDF8" }} />
                </div>
                <span className="text-[9px] font-mono text-gray-500">{Math.round((r.raised / Math.max(1, r.target)) * 100)}%</span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[9px] font-mono text-gray-500">
                <span>估值 ¥{r.valuation}万</span>
                <span>稀释 {r.dilution}%</span>
                <span>资金跑道 {r.runway} 个月</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showReport && (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">投资人报告 · 5 年财务预测</span>
          </div>
          <div className="grid grid-cols-5 gap-2 text-center">
            {NPV_DATA.map((d) => (
              <div key={d.year} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
                <div className="text-[8px] font-mono text-gray-600">第{d.year}年</div>
                <div className="text-[9px] font-mono text-emerald-400">¥{d.revenue}万</div>
                <div className="text-[8px] font-mono text-gray-500">FCF ¥{d.fcf}万</div>
              </div>
            ))}
          </div>
          <div className="text-[9px] font-mono text-gray-500 mt-2">关键指标：NPV（15%）≈ ¥{(dcf / 100).toFixed(0)}万 · IRR 约 42% · 回收期 2.8 年</div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

